/**
 * @fileoverview GitHub Metrics Dashboard Page
 * @description Redesigned professional dashboard with Alaska Airlines styling
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  Code, 
  TrendingUp, 
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  RefreshCw,
  AlertCircle,
  Bot,
  Trash2,
  Cpu,
  Languages,
  Sparkles
} from 'lucide-react';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  getDailyUserTrends,
  getEditorDistribution,
  getLanguageDistribution,
  getRepositoryActivity,
  getWeeklyTrends,
  getChatStatistics,
  getAcceptanceRates,
  getSummaryStats,
} from '../utils';
import { useGitHub } from '@/hooks/useGitHub';
import { githubUserMetricsApi } from '@/services/api/githubUserMetrics';
import {
  getLanguageUsageAcrossUsers,
  getModelUsageAcrossUsers,
  getFeatureUsageAcrossUsers,
} from '@/features/GitHubUserMetrics/utils';
import { appInsights } from '@/telemetry';
import './Dashboard.css';

const GITHUB_ORG = import.meta.env.VITE_GITHUB_ORG || 'Alaska-ITS';

const presetRanges = [
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 14 Days', days: 14 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 60 Days', days: 60 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'All Time', days: null },
];

const tabs = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'editors', label: 'Editors & Languages', icon: Code },
  { id: 'completions', label: 'Code Completions', icon: BarChart3 },
  { id: 'ide-codegen', label: 'IDE Code Generation', icon: Sparkles },
];

// Compact number for stat tiles (1.5M, 557.1K); full value shown on hover.
const formatStatValue = (v) => {
  if (typeof v !== 'number') return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtitle, color, bgColor }) => (
  <div className="stat-card" style={{ '--stat-color': color, '--stat-bg': bgColor }}>
    <div className="stat-card-icon">
      <Icon size={20} />
    </div>
    <div className="stat-card-content">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value" title={typeof value === 'number' ? value.toLocaleString() : String(value)}>
        {formatStatValue(value)}
      </div>
      {subtitle && <div className="stat-card-subtitle">{subtitle}</div>}
    </div>
  </div>
);

// Chart Card Component
const ChartCard = ({ title, icon: Icon, children, actions, footer }) => (
  <div className="chart-card">
    <div className="chart-card-header">
      <h3 className="chart-card-title">
        {Icon && <Icon size={20} />}
        {title}
      </h3>
      {actions && <div className="chart-card-actions">{actions}</div>}
    </div>
    <div className="chart-content">{children}</div>
    {footer && <div className="chart-footer">{footer}</div>}
  </div>
);

// Loading Skeleton
const SkeletonCard = () => <div className="skeleton skeleton-card" />;
const SkeletonChart = () => <div className="skeleton skeleton-chart" />;

// Chart Placeholder for errors
const ChartPlaceholder = ({ message = 'Unable to load chart' }) => (
  <div className="chart-placeholder">
    <AlertCircle size={32} className="chart-placeholder-icon" />
    <span>{message}</span>
  </div>
);

// ── IDE code generation (real QE-member data) ───────────────
const ideCodeGenColors = {
  suggested: '#86efac',    // light green
  userAdded: '#15803d',    // dark green
  agentAdded: '#c4b5fd',   // light violet
  agentDeleted: '#7c3aed', // violet
};

// Compact k/m axis ticks (e.g. 50k, 1.3m).
const formatCompactAxis = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}m`;
  if (abs >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n}`;
};

// Small square-swatch legend rendered above each chart.
const IdeCodeGenLegend = ({ items }) => (
  <div className="idecg-legend">
    {items.map((item) => (
      <span key={item.label} className="idecg-legend-item">
        <span className="idecg-legend-swatch" style={{ backgroundColor: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);

// Horizontal dashed gridlines only, muted 11px ticks, no y-axis line.
const ideCodeGenChartSx = {
  '& .MuiChartsGrid-line': { strokeDasharray: '4 4', stroke: '#e2e8f0' },
  '& .MuiChartsAxis-tickLabel': { fill: '#94a3b8', fontSize: 11 },
  '& .MuiChartsAxis-left .MuiChartsAxis-line': { display: 'none' },
  '& .MuiChartsAxis-left .MuiChartsAxis-tick': { display: 'none' },
  '& .MuiChartsAxis-bottom .MuiChartsAxis-line': { stroke: '#e2e8f0' },
  '& .MuiChartsAxis-bottom .MuiChartsAxis-tick': { display: 'none' },
};

// Grouped bar chart. MUI X has no maxBarSize; gap ratios approximate the ~38px bar width.
const IdeCodeGenBarChart = ({ categories, series, height = 300 }) => (
  <BarChart
    height={height}
    xAxis={[{
      scaleType: 'band',
      data: categories,
      categoryGapRatio: 0.4,
      barGapRatio: 0.2,
      tickLabelInterval: () => true,
      valueFormatter: (value, context) => {
        const s = String(value);
        return context?.location === 'tick' && s.length > 12 ? `${s.slice(0, 11)}…` : s;
      },
    }]}
    yAxis={[{
      width: 48,
      disableLine: true,
      disableTicks: true,
      valueFormatter: formatCompactAxis,
    }]}
    series={series.map((s) => ({
      ...s,
      valueFormatter: (value) => (value == null ? '' : Number(value).toLocaleString()),
    }))}
    grid={{ horizontal: true }}
    borderRadius={3}
    hideLegend
    margin={{ top: 10, right: 12, bottom: 32, left: 8 }}
    sx={ideCodeGenChartSx}
  />
);

// Real Copilot metrics from the DB (backfilled from Jan 2026); month + scope re-fetch from the API.
const IdeCodeGenerationSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('all');
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await githubUserMetricsApi.getAvailableMonths();
        const list = (resp?.data ?? resp)?.months ?? [];
        if (cancelled) return;
        setMonths(list);
        if (list.length) setMonth((m) => m || list[0]);
      } catch { /* month list is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await githubUserMetricsApi.getAllMetrics(false, month || null, scope);
        const payload = resp?.data ?? resp;
        if (!cancelled) setUsers(payload?.users ?? []);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load member metrics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, month]);

  const totals = useMemo(() => {
    let added = 0, suggested = 0, deleted = 0;
    for (const u of users) {
      added += u.usage?.loc_added || 0;
      suggested += u.usage?.loc_suggested || 0;
      deleted += u.usage?.loc_deleted || 0;
    }
    return { added, suggested, deleted, changed: added + deleted };
  }, [users]);

  const daily = useMemo(() => {
    const map = new Map();
    for (const u of users) {
      for (const d of (u.usage?.daily_loc || [])) {
        const cur = map.get(d.date) || { added: 0, deleted: 0, suggested: 0 };
        cur.added += d.loc_added || 0;
        cur.deleted += d.loc_deleted || 0;
        cur.suggested += d.loc_suggested || 0;
        map.set(d.date, cur);
      }
    }
    return [...map.entries()]
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      .map(([date, v]) => ({ date, ...v }));
  }, [users]);

  const langUsage = useMemo(() => getLanguageUsageAcrossUsers(users).slice(0, 8), [users]);
  const modelUsage = useMemo(() => getModelUsageAcrossUsers(users).slice(0, 8), [users]);
  const featureUsage = useMemo(() => getFeatureUsageAcrossUsers(users), [users]);

  const fmtDay = (iso) => {
    const p = String(iso).split('-');
    return p.length === 3 ? `${Number(p[1])}/${Number(p[2])}` : iso;
  };
  const fmtMonth = (m) => {
    const p = String(m).split('-');
    if (p.length < 2) return m;
    return new Date(Number(p[0]), Number(p[1]) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <section className="idecg-section">
      <div className="idecg-header">
        <p className="idecg-note">
          {scope === 'qe' ? 'QE members (tracked users)' : 'All org members'}{month ? ` · ${fmtMonth(month)}` : ''}{!loading && !error && users.length ? ` · ${users.length} member${users.length === 1 ? '' : 's'}` : ''}.
        </p>
        <div className="idecg-controls">
          <select className="chart-filter-select" value={month} onChange={(e) => setMonth(e.target.value)} disabled={!months.length}>
            {months.length === 0 && <option value="">Current month</option>}
            {months.map((m) => (<option key={m} value={m}>{fmtMonth(m)}</option>))}
          </select>
          <select className="chart-filter-select" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="all">All</option>
            <option value="qe">QE</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="idecg-loading">Loading {scope === 'qe' ? 'QE' : 'org'} metrics…</div>
      ) : error ? (
        <ChartPlaceholder message={error} />
      ) : !users.length ? (
        <ChartPlaceholder message={scope === 'qe' ? 'No tracked QE members with data for this month.' : 'No member metrics for this month.'} />
      ) : (
        <>
          {/* Stat tiles */}
          <div className="stats-grid">
            <StatCard icon={Sparkles} label="Lines of code changed with AI" value={totals.changed} subtitle="AI lines added + deleted across all modes" color="#0d9488" bgColor="#ccfbf1" />
            <StatCard icon={Code} label="Lines added" value={totals.added} subtitle="Total AI lines added" color="#16a34a" bgColor="#dcfce7" />
            <StatCard icon={TrendingUp} label="Lines suggested" value={totals.suggested} subtitle="Total AI lines suggested" color="#0097A9" bgColor="#E0F7FA" />
            <StatCard icon={Trash2} label="Lines deleted" value={totals.deleted} subtitle="Total lines deleted across all modes" color="#7c3aed" bgColor="#ede9fe" />
          </div>

          {/* Daily added & deleted — full width */}
          <div className="charts-grid charts-grid-1">
            <ChartCard title="Daily AI lines added & deleted" icon={BarChart3}>
              <IdeCodeGenLegend
                items={[
                  { label: 'Added', color: ideCodeGenColors.userAdded },
                  { label: 'Deleted', color: ideCodeGenColors.agentDeleted },
                ]}
              />
              <IdeCodeGenBarChart
                height={320}
                categories={daily.map((d) => fmtDay(d.date))}
                series={[
                  { data: daily.map((d) => d.added), label: 'Added', color: ideCodeGenColors.userAdded },
                  { data: daily.map((d) => d.deleted), label: 'Deleted', color: ideCodeGenColors.agentDeleted },
                ]}
              />
            </ChartCard>
          </div>

          {/* Breakdowns — activity counts from the report */}
          <div className="charts-grid charts-grid-2" style={{ marginTop: 24 }}>
            <ChartCard title="Acceptances by language" icon={Languages}>
              <IdeCodeGenLegend items={[{ label: 'Acceptances', color: ideCodeGenColors.userAdded }]} />
              <IdeCodeGenBarChart
                categories={langUsage.map((l) => l.label)}
                series={[{ data: langUsage.map((l) => l.value), label: 'Acceptances', color: ideCodeGenColors.userAdded }]}
              />
            </ChartCard>
            <ChartCard title="Requests by model" icon={Cpu}>
              <IdeCodeGenLegend items={[{ label: 'Requests', color: ideCodeGenColors.agentDeleted }]} />
              <IdeCodeGenBarChart
                categories={modelUsage.map((m) => m.label)}
                series={[{ data: modelUsage.map((m) => m.value), label: 'Requests', color: ideCodeGenColors.agentDeleted }]}
              />
            </ChartCard>
          </div>

          {/* Activity by feature / mode */}
          <div className="charts-grid charts-grid-1" style={{ marginTop: 24 }}>
            <ChartCard title="Activity by feature / mode" icon={Bot}>
              <IdeCodeGenLegend items={[{ label: 'Activity', color: ideCodeGenColors.agentAdded }]} />
              <IdeCodeGenBarChart
                categories={featureUsage.map((f) => f.label)}
                series={[{ data: featureUsage.map((f) => f.value), label: 'Activity', color: ideCodeGenColors.agentAdded }]}
              />
            </ChartCard>
          </div>
        </>
      )}
    </section>
  );
};

export const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState('All Time');
  const [copilotData, setCopilotData] = useState([]);
  
  // Chart filter states
  const [languageTopN, setLanguageTopN] = useState(10);
  const [editorTopN, setEditorTopN] = useState(10);

  const { fetchCopilotMetrics } = useGitHub();

  // Fetch data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const metricsData = await fetchCopilotMetrics(GITHUB_ORG);
        const metrics = Array.isArray(metricsData) ? metricsData
          : Array.isArray(metricsData?.items) ? metricsData.items
          : null;
        if (metrics) {
          setCopilotData(metrics);
        } else {
          setError('Invalid data format received from API');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch Copilot metrics');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchCopilotMetrics]);

  // Date range from data
  const dateRange = useMemo(() => {
    if (!copilotData || copilotData.length === 0) return { min: null, max: null };
    const dates = copilotData.map((d) => new Date(d.date));
    return {
      min: new Date(Math.min(...dates)),
      max: new Date(Math.max(...dates)),
    };
  }, [copilotData]);

  // Filter data by date range
  const filteredRawData = useMemo(() => {
    if (!copilotData) return [];
    return copilotData.filter((day) => {
      const date = new Date(day.date);
      if (startDate && date < startDate) return false;
      if (endDate && date > endDate) return false;
      return true;
    });
  }, [copilotData, startDate, endDate]);

  // Transform data
  const data = useMemo(() => {
    if (filteredRawData.length === 0) return null;
    try {
      return {
        dailyTrends: getDailyUserTrends(filteredRawData),
        editorDistribution: getEditorDistribution(filteredRawData),
        languageDistribution: getLanguageDistribution(filteredRawData),
        repositoryActivity: getRepositoryActivity(filteredRawData),
        weeklyTrends: getWeeklyTrends(filteredRawData),
        chatStats: getChatStatistics(filteredRawData),
        acceptanceRates: getAcceptanceRates(filteredRawData),
        summaryStats: getSummaryStats(filteredRawData),
      };
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [filteredRawData]);

  // Handlers
  const handlePresetClick = (preset) => {
    setActivePreset(preset.label);
    if (preset.days === null) {
      setStartDate(null);
      setEndDate(null);
    } else {
      const end = dateRange.max ? new Date(dateRange.max) : new Date();
      const start = new Date(end);
      start.setDate(end.getDate() - preset.days);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleRetry = async () => {
    setError(null);
    setShowError(true);
    setLoading(true);
    try {
      const metricsData = await fetchCopilotMetrics(GITHUB_ORG);
      const metrics = Array.isArray(metricsData) ? metricsData
        : Array.isArray(metricsData?.items) ? metricsData.items
        : null;
      if (metrics) {
        setCopilotData(metrics);
      } else {
        setError('Invalid data format received from API');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch Copilot metrics');
      appInsights.trackException({
        exception: err instanceof Error ? err : new Error(err?.message || 'Unknown error'),
        properties: {
          name: 'DataFetchError',
          message: err?.message || 'Unknown error',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (error) setShowError(true);
  }, [error]);

  const hasData = data !== null;

  // Chart colors
  const chartColors = ['#0097A9', '#01426A', '#00AF66', '#FF6B35', '#6366F1', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444'];

  // Render stats cards
  const renderStats = () => {
    if (loading) {
      return (
        <div className="stats-grid">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
      );
    }

    if (!data?.summaryStats) return null;

    const stats = data.summaryStats;
    return (
      <div className="stats-grid">
        <StatCard
          icon={Users}
          label="Avg Daily Active Users"
          value={stats.avgActiveUsers}
          subtitle={`Peak: ${stats.maxActiveUsers}`}
          color="#0097A9"
          bgColor="#E0F7FA"
        />
        <StatCard
          icon={TrendingUp}
          label="Avg Daily Engaged Users"
          value={stats.avgEngagedUsers}
          subtitle={`Peak: ${stats.maxEngagedUsers}`}
          color="#00AF66"
          bgColor="#E8F5E9"
        />
        <StatCard
          icon={MessageSquare}
          label="Avg IDE Chat Users"
          value={stats.avgIdeChatUsers}
          subtitle={`Total: ${stats.totalIdeChatUsers.toLocaleString()}`}
          color="#FF6B35"
          bgColor="#FFF3E0"
        />
        <StatCard
          icon={Code}
          label="Avg Code Completion Users"
          value={stats.avgCodeCompletionUsers}
          subtitle={`Total: ${stats.totalCodeCompletionUsers.toLocaleString()}`}
          color="#6366F1"
          bgColor="#EDE7F6"
        />
      </div>
    );
  };

  // Editor Pie Chart
  const renderEditorChart = (height = 300) => {
    if (!data?.editorDistribution?.length) return <ChartPlaceholder />;
    const filteredData = editorTopN !== null ? data.editorDistribution.slice(0, editorTopN) : data.editorDistribution;
    return (
      <PieChart
        series={[{
          data: filteredData.map((e, i) => ({ id: i, value: e.totalUsers, label: e.name })),
          innerRadius: 50,
          outerRadius: 100,
          paddingAngle: 2,
          cornerRadius: 4,
        }]}
        colors={chartColors}
        height={height}
        margin={{ right: 150 }}
        slotProps={{
          legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' } },
        }}
      />
    );
  };

  // Acceptance Gauges
  const renderAcceptanceGauges = () => {
    if (!data?.acceptanceRates?.length) return <ChartPlaceholder />;
    const totals = data.acceptanceRates.reduce(
      (acc, d) => ({
        acceptances: acc.acceptances + (d.totalAcceptances || 0),
        suggestions: acc.suggestions + (d.totalSuggestions || 0),
        linesAccepted: acc.linesAccepted + (d.totalLinesAccepted || 0),
        linesSuggested: acc.linesSuggested + (d.totalLinesSuggested || 0),
      }),
      { acceptances: 0, suggestions: 0, linesAccepted: 0, linesSuggested: 0 }
    );
    const acceptanceRate = totals.suggestions > 0 ? (totals.acceptances / totals.suggestions) * 100 : 0;
    const lineRate = totals.linesSuggested > 0 ? (totals.linesAccepted / totals.linesSuggested) * 100 : 0;

    return (
      <div className="gauges-container">
        <div className="gauge-item">
          <div className="gauge-label">Suggestion Acceptance Rate</div>
          <Gauge
            value={acceptanceRate}
            startAngle={-110}
            endAngle={110}
            height={200}
            sx={{
              [`& .${gaugeClasses.valueText}`]: { fontSize: 28, fontWeight: 700 },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: acceptanceRate > 30 ? '#00AF66' : acceptanceRate > 20 ? '#FF6B35' : '#EF4444',
              },
            }}
            text={({ value }) => `${value.toFixed(1)}%`}
          />
        </div>
        <div className="gauge-item">
          <div className="gauge-label">Line Acceptance Rate</div>
          <Gauge
            value={lineRate}
            startAngle={-110}
            endAngle={110}
            height={200}
            sx={{
              [`& .${gaugeClasses.valueText}`]: { fontSize: 28, fontWeight: 700 },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: lineRate > 30 ? '#00AF66' : lineRate > 20 ? '#FF6B35' : '#EF4444',
              },
            }}
            text={({ value }) => `${value.toFixed(1)}%`}
          />
        </div>
      </div>
    );
  };

  // Lines Sparklines
  const renderLinesSparklines = () => {
    if (!data?.acceptanceRates?.length) return <ChartPlaceholder />;
    const filtered = data.acceptanceRates.filter((d) => d.totalLinesSuggested > 0);
    if (filtered.length === 0) return <ChartPlaceholder message="No lines data available" />;
    const suggested = filtered.map((d) => d.totalLinesSuggested);
    const accepted = filtered.map((d) => d.totalLinesAccepted);
    const totalSuggested = suggested.reduce((a, b) => a + b, 0);
    const totalAccepted = accepted.reduce((a, b) => a + b, 0);

    return (
      <div className="sparklines-container">
        <div className="sparkline-item">
          <div className="sparkline-label">Lines Suggested</div>
          <div className="sparkline-value" style={{ color: '#0097A9' }}>{totalSuggested.toLocaleString()}</div>
          <SparkLineChart data={suggested} height={120} curve="natural" area colors={['#0097A9']} showHighlight showTooltip />
        </div>
        <div className="sparkline-item">
          <div className="sparkline-label">Lines Accepted</div>
          <div className="sparkline-value" style={{ color: '#00AF66' }}>{totalAccepted.toLocaleString()}</div>
          <SparkLineChart data={accepted} height={120} curve="natural" area colors={['#00AF66']} showHighlight showTooltip />
        </div>
        <div className="sparkline-item sparkline-rate">
          <div className="sparkline-label">Acceptance Ratio</div>
          <div className="sparkline-value" style={{ color: '#6366F1' }}>
            {totalSuggested > 0 ? ((totalAccepted / totalSuggested) * 100).toFixed(1) : 0}%
          </div>
        </div>
      </div>
    );
  };

  // Language Acceptance Chart
  const renderLanguageAcceptanceChart = (height = 350) => {
    if (!data?.languageDistribution?.length) return <ChartPlaceholder />;
    const filtered = languageTopN !== null ? data.languageDistribution.slice(0, languageTopN) : data.languageDistribution;
    return (
      <BarChart
        xAxis={[{ scaleType: 'band', data: filtered.map((l) => l.name) }]}
        series={[
          { data: filtered.map((l) => l.acceptanceRate || 0), label: 'Acceptance Rate %', color: '#6366F1' },
        ]}
        height={height}
        margin={{ left: 60, right: 20, top: 20, bottom: 70 }}
      />
    );
  };

  // Filter Select Component
  const FilterSelect = ({ value, onChange, options }) => (
    <select 
      className="chart-filter-select" 
      value={value ?? ''} 
      onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value))}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );

  const topNOptions = [
    { value: 5, label: 'Top 5' },
    { value: 10, label: 'Top 10' },
    { value: 15, label: 'Top 15' },
    { value: '', label: 'All' },
  ];

  // Tab Content
  const renderTabContent = () => {
    // Uses its own placeholder data, so it renders regardless of API load state.
    if (activeTab === 'ide-codegen') {
      return <IdeCodeGenerationSection />;
    }

    if (loading) {
      return (
        <div className="charts-grid charts-grid-2">
          <SkeletonChart />
          <SkeletonChart />
        </div>
      );
    }

    if (!hasData) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className="charts-grid charts-grid-2">
              <ChartCard title="Acceptance Rates" icon={BarChart3}>
                {renderAcceptanceGauges()}
              </ChartCard>
              <ChartCard title="Lines of Code Trends" icon={Code}>
                {renderLinesSparklines()}
              </ChartCard>
            </div>
            <div className="charts-grid charts-grid-2" style={{ marginTop: 24 }}>
              <ChartCard 
                title="Editor Distribution" 
                icon={PieChartIcon}
                actions={<FilterSelect value={editorTopN} onChange={setEditorTopN} options={topNOptions} />}
              >
                {renderEditorChart()}
              </ChartCard>
            </div>
          </>
        );

      case 'editors':
        return (
          <>
            <div className="charts-grid charts-grid-2">
              <ChartCard 
                title="Language Acceptance Rates" 
                icon={BarChart3}
                actions={<FilterSelect value={languageTopN} onChange={setLanguageTopN} options={topNOptions} />}
              >
                {renderLanguageAcceptanceChart()}
              </ChartCard>
              <ChartCard 
                title="Editor Distribution" 
                icon={PieChartIcon}
                actions={<FilterSelect value={editorTopN} onChange={setEditorTopN} options={topNOptions} />}
              >
                {renderEditorChart()}
              </ChartCard>
            </div>
          </>
        );

      case 'completions':
        return (
          <>
            <div className="charts-grid charts-grid-2">
              <ChartCard title="Acceptance Rates" icon={BarChart3}>
                {renderAcceptanceGauges()}
              </ChartCard>
              <ChartCard title="Lines of Code Trends" icon={Code}>
                {renderLinesSparklines()}
              </ChartCard>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="github-metrics-page">
        {/* Page Header */}
        <div className="metrics-page-header">
          <h1 className="metrics-page-title">GitHub Copilot Analytics</h1>
          <p className="metrics-page-subtitle">
            Track GitHub Copilot usage, acceptance rates, and productivity metrics across your organization.
          </p>
        </div>

        {/* Error Alert */}
        {error && showError && (
          <div className="error-container">
            <div className="error-icon">
              <AlertCircle size={24} />
            </div>
            <div className="error-content">
              <h4 className="error-title">Failed to load data</h4>
              <p className="error-message">{error}</p>
            </div>
            <div className="error-actions">
              <button className="retry-button" onClick={handleRetry} disabled={loading}>
                <RefreshCw size={16} />
                {loading ? 'Retrying...' : 'Retry'}
              </button>
              <button className="dismiss-button" onClick={() => setShowError(false)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Date Filter Section */}
        <div className="date-filter-section">
          <div className="date-filter-header">
            <h4 className="date-filter-title">
              <Calendar size={18} />
              Filter by Date Range
            </h4>
            <div className="date-filter-controls">
              <div className="preset-chips">
                {presetRanges.map((preset) => (
                  <button
                    key={preset.label}
                    className={`preset-chip ${activePreset === preset.label ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="date-pickers">
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => { setStartDate(date); setActivePreset(null); }}
                  minDate={dateRange.min}
                  maxDate={endDate || dateRange.max}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 160, background: 'white' } },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => { setEndDate(date); setActivePreset(null); }}
                  minDate={startDate || dateRange.min}
                  maxDate={dateRange.max}
                  slotProps={{
                    textField: { size: 'small', sx: { width: 160, background: 'white' } },
                  }}
                />
              </div>
              <div className="date-range-info">
                Showing {filteredRawData.length} of {copilotData.length} days
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {renderStats()}

        {/* Tabs Navigation */}
        <div className="tabs-container">
          <div className="tabs-list">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Empty State */}
        {!loading && !error && !hasData && copilotData.length > 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Calendar size={40} />
            </div>
            <h3 className="empty-state-title">No data for selected range</h3>
            <p className="empty-state-message">Try adjusting the date filters to see data.</p>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default Dashboard;
